/* eslint-disable tailwindcss/no-custom-classname */
import React, {
  forwardRef, useContext, useEffect, useMemo, Fragment, useRef, useCallback, useState
} from 'react';
import proptypes from 'prop-types';
import {
  QueryContext, QueryProvider, extend,
  params,
  string as stringType,
  number as numberType,
  shape as shapeType,
  customType,
} from '@thd-nucleus/data-sources';
import { AppContext } from '@thd-nucleus/app-render';

import {
  Skeleton,
  SkeletonBlock,
  SkeletonLine
} from '@one-thd/sui-atomic-components';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { Col } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ResultsWrapped } from '../ResultsWrapped';
import { ProductResultsContext } from '../ProductResultsContext';
import { ScreenSize } from '../../product-results-helpers';
import { sendSponsoredContentAnalytics } from '../../utils/sendSponsoredContentAnalytics';
import { getIsSkipKPF } from '../../utils/keyProductFeaturePodCategories';
import { useProductsPerRow } from './hooks/useProductsPerRow';
import { useHybridData } from '../../hooks/useHybridData';
import { useProductResults } from '../../hooks/useProductResults';
import useProductResultsParamsPLP from '../../hooks/useProductResultsParamsPLP';
import {
  DEFAULT_LAYOUT_STYLE, LIST_LAYOUT_STYLE, MAX_PODS_TO_HYDRATE, HYDRATION_STANDARD_SCROLL_BUFFER
} from '../../utils/constants';

const {
  string,
  func,
  arrayOf,
  shape,
  bool,
  node,
  number
} = proptypes;

const ProductResultsGrid = forwardRef(({
  productSSR,
  searchSSR,
  pageType,
  defaultVariables,
  podFunction,
  slots,
  keyword,
  storeId,
  itemId,
  useTwoColumnPods,
  experienceType
}, ref) => {
  const { instance } = useContext(AppContext);

  const {
    data: wrapperData,
    loading: wrapperLoading,
    responseLoading: wrapperResponseLoading
  } = useContext(ProductResultsContext);

  const { isClientResolved, defaultVariables: queryDefaultVariables } = useContext(QueryContext);
  const { channel, customer, isServer } = useContext(ExperienceContext);
  const { isCustomerIdentified = false } = useThdCustomer();
  const isComponentMountedRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const isSearch = pageType === 'search';
  const isPep = pageType === 'pep';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const { isLocalized, storeZip } = useStore({ varnish, online: false });
  const store = useStore({ varnish, online: false });
  const localizedAndClientLoaded = useRef(false);
  const [layoutStyle, setLayoutStyle] = useState(DEFAULT_LAYOUT_STYLE);
  const isListLayout = layoutStyle === LIST_LAYOUT_STYLE;
  const podListClass = useTwoColumnPods ? 'lg:sui-col-span-2 xl:sui-col-span-1 sui-mb-2' : 'sui-col-span-4';
  const gridListClass = useTwoColumnPods ? 'sui-grid sui-gap-2 sm:sui-grid-cols-2 lg:sui-grid-cols-1 xl:sui-grid-cols-2' : 'sui-grid sui-pl-2 sui-w-full';
  const isB2B = customer?.type === 'b2b';
  const initialStoreRef = useRef();
  if (!initialStoreRef.current) {
    initialStoreRef.current = store?.storeId || storeId;
  }

  const initialIsControlledDataRef = useRef();
  if (initialIsControlledDataRef.current === undefined) {
    initialIsControlledDataRef.current = Boolean(
      (wrapperData && typeof wrapperData === 'object' && Object.keys(wrapperData).length > 0)
      || wrapperLoading
      || wrapperResponseLoading
    );
  }

  const isControlledData = initialIsControlledDataRef.current;

  const paramsPLP = useProductResultsParamsPLP({ pageType });

  const hybridData = useHybridData({
    isShowProducts: paramsPLP.isShowProducts,
    navParam: paramsPLP.nValue,
    isSearch,
    isSkip: isPep
  }) || {};

  const customContextValues = {
    pageType,
    isSearch,
    varnish,
    isBrowse: pageType === 'browse',
    path: paramsPLP.path || '',
    ...hybridData
  };

  const keyProductFeaturePodCategoriesConfig = useConfigService('KEY_PRODUCT_FEATURE_POD_CATEGORIES');
  const isSkipKPF = getIsSkipKPF({
    categoriesConfig: keyProductFeaturePodCategoriesConfig,
    navValue: paramsPLP?.nValue,
    isB2B,
    channel,
    experienceType
  });

  const selfFetchedData = useProductResults({
    itemId,
    productSSR,
    searchSSR,
    pageType,
    skip: isControlledData,
    customCtxInitialValue: customContextValues,
    shouldUpdateCustomContextProp: true,
    isSkipKPF,
    ...paramsPLP,
  });

  useEffect(() => {
    isComponentMountedRef.current = true;
    setIsHydrated(true);
  }, []);

  let data = isControlledData ? wrapperData : selfFetchedData.data;
  let loading = isControlledData ? wrapperLoading : selfFetchedData.loading;
  let responseLoading = isControlledData ? wrapperResponseLoading : selfFetchedData.responseLoading;

  // Send sponsored product render rate metrics when not using ProductResults wrapper
  useEffect(() => {
    if (!isControlledData && data?.searchModel?.products) {
      sendSponsoredContentAnalytics(data.searchModel.products, loading);
    }
  }, [isControlledData, data?.searchModel?.products, loading]);

  // Listen to PLP_LAYOUTSTYLE.value event to change the type of CustomProductPod to display
  useEffect(() => {
    if (
      typeof window === 'undefined'
      || !window.LIFE_CYCLE_EVENT_BUS
      || typeof window.LIFE_CYCLE_EVENT_BUS.on !== 'function'
    ) {
      return undefined;
    }
    const layoutStyleHandler = ({ output } = {}) => {
      const layoutStyleValue = (output?.layoutStyle) || DEFAULT_LAYOUT_STYLE;
      setLayoutStyle(layoutStyleValue);
    };

    window.LIFE_CYCLE_EVENT_BUS.on('PLP_LAYOUTSTYLE.value', layoutStyleHandler);

    return () => {
      if (typeof window !== 'undefined'
        && window.LIFE_CYCLE_EVENT_BUS
        && typeof window.LIFE_CYCLE_EVENT_BUS.off === 'function') {
        window.LIFE_CYCLE_EVENT_BUS.off('PLP_LAYOUTSTYLE.value', layoutStyleHandler);
      }
    };
  }, []);

  const isLocalizationReady = isComponentMountedRef.current && data && Object.keys(data).length > 0
    && isLocalized && !loading && !responseLoading;

  const isDesktop = channel === 'desktop';

  const { products = [] } = data?.searchModel || {};

  const shouldShowPlaceholders = loading && (!products || products.length === 0);
  const shouldLimitServerSearchPods = !isHydrated && !instance?.thdSEOBotDetection && isSearch;
  const [productsPerRow, matchesScreen] = useProductsPerRow({ channel });
  const componentsToRender = useMemo(() => {
    let screen = matchesScreen.current;

    if ((matchesScreen.current === ScreenSize.DX_LARGE || typeof window === 'undefined')
      && channel === 'desktop') {
      screen = ScreenSize.X_LARGE;
    }

    return slots?.reduce((res, slot) => {
      const position = slot.position[screen] || slot.position[ScreenSize.UNPREFIXED];
      res[position] = res[position] ? [...res[position], slot] : [slot];
      return res;
    }, []);
  }, [matchesScreen.current, slots]);

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) {
      return false; // Don't mount if store not localized
    }
    if (responseLoading) {
      return false; // Don't mount during response loading
    }
    return mounted;
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip; // Only control product queries
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });

    if (attributes.product.fulfillment) {
      if (isResolved) {
        return skip; // Skip if search model resolved
      }
      return true; // Skip fulfillment queries until search model ready
    }
    return skip;
  };

  const defaultVariablesFallback = useMemo(() => {
    const baseVariables = {
      storeId,
      skipKPF: true,
      skipSubscribeAndSave: true,
      skipInstallServices: false
    };
    // Only include zipCode if queryDefaultVariables has the property
    if (queryDefaultVariables?.current?.hasOwnProperty('zipCode')) {
      baseVariables.zipCode = storeZip;
    }
    return baseVariables;
  }, [storeId, queryDefaultVariables]);
  const enhancedDefaultVariables = useMemo(() => {
    // Sync QueryProvider defaults with useProductResults variables
    if (isLocalizationReady) {
      localizedAndClientLoaded.current = true;
      const baseVariables = {
        ...defaultVariables,
        storeId: store?.storeId,
        isBrandPricingPolicyCompliant: isB2B ? true : isCustomerIdentified,
        skipKPF: isSkipKPF
      };
      // Only include zipCode if queryDefaultVariables has the property
      if (queryDefaultVariables?.current?.hasOwnProperty('zipCode')) {
        baseVariables.zipCode = storeZip;
      }
      return baseVariables;
    }
    const baseVariables = {
      ...defaultVariables,
      storeId: localizedAndClientLoaded.current ? store?.storeId : initialStoreRef.current,
      isBrandPricingPolicyCompliant: isB2B || (localizedAndClientLoaded.current && isCustomerIdentified)
    };
    // Only include zipCode if queryDefaultVariables has the property
    if (queryDefaultVariables?.current?.hasOwnProperty('zipCode')) {
      baseVariables.zipCode = storeZip;
    }
    return baseVariables;
  }, [isControlledData, defaultVariables, isCustomerIdentified, initialStoreRef.current, store, isLocalizationReady, queryDefaultVariables]);
  /**
     * PRODUCT SETS CALCULATION: Groups products into sets for rendering with slot insertion
     *
     * Returns an array of objects containing:
     * - start: starting index for product pod loop
     * - productSetSize: how many products are grouped together
     * - slotIndex: the index for promotional/content component to be rendered in the slot
     *
     * This allows promotional content (ads, banners) to be inserted between product rows
     * while maintaining proper grid layout and responsive behavior.
     */
  const productSets = useMemo(() => {
    if (!products?.length) {
      return [];
    }

    const sets = [];
    const clampedPositions = [];
    let currentIndex = 0;
    // Insert slot components at calculated positions based on products per row
    if (componentsToRender?.length > 0) {
      componentsToRender.forEach((slotsAtPosition, i) => {
        if (!slotsAtPosition) {
          return;
        }

        const nextPosition = i * productsPerRow;
        const withinProducts = nextPosition <= products.length;

        // clampToEnd renders after the last product; deferred so products keep their row-sized sets.
        if (!withinProducts && slotsAtPosition.some((slot) => slot?.clampToEnd)) {
          clampedPositions.push(i);
          return;
        }

        if (currentIndex >= products.length || nextPosition <= currentIndex) {
          return;
        }

        sets.push({
          start: currentIndex,
          productSetSize: Math.min(nextPosition, products.length) - currentIndex,
          slotIndex: withinProducts ? i : -1,
          endClamped: false
        });

        currentIndex = Math.min(nextPosition, products.length);
      });
    }

    // Add remaining products after all slots are placed
    while (currentIndex < products.length) {
      sets.push({
        start: currentIndex,
        productSetSize: Math.min(productsPerRow, products.length - currentIndex),
        slotIndex: -1 // No slot component for this set
      });

      currentIndex += productsPerRow;
    }

    clampedPositions.forEach((i) => {
      sets.push({
        start: products.length, productSetSize: 0, slotIndex: i, endClamped: true
      });
    });

    return sets;
  }, [productsPerRow, products?.length, componentsToRender]);

  /**
   * SERVER POD LIMITING: During SSR (before client hydration), restricts the rendered product
   * sets to a small number of pods (4 desktop / 2 mobile) to keep the initial HTML payload
   * lean. Once the component mounts on the client, isHydrated becomes true and all product
   * sets are returned so the full progressive hydration chunking can take over.
   */
  const visibleProductSets = useMemo(() => {
    if (!shouldLimitServerSearchPods) return productSets;
    const serverPodLimit = isDesktop ? 4 : 2;
    let totalPods = 0;
    return productSets.reduce((acc, set) => {
      if (totalPods >= serverPodLimit) return acc;
      const podsToAdd = Math.min(set.productSetSize, serverPodLimit - totalPods);
      totalPods += podsToAdd;
      acc.push({ ...set, productSetSize: podsToAdd });
      return acc;
    }, []);
  }, [productSets, shouldLimitServerSearchPods, isDesktop]);

  /**
   * PROGRESSIVE HYDRATION: Groups product sets into hydrator-sized chunks.
   *
   * Each chunk wraps up to MAX_PODS_TO_HYDRATE products (12 desktop / 6 mobile).
   * - First chunk uses patch={true} → hydrates immediately on client mount.
   * - Subsequent chunks use patch={false} → deferred via IntersectionObserver
   *   with a 1500px scroll buffer, so below-the-fold pods only hydrate as the
   *   user scrolls toward them.
   *
   * Slot components (ads, discovery zones) are preserved: they remain attached
   * to the product set they logically follow and are lifted into the chunk that
   * contains that set.
   */
  const hydratorChunks = useMemo(() => {
    if (!visibleProductSets?.length) return [];
    const maxPodsPerChunk = isDesktop ? MAX_PODS_TO_HYDRATE.DESKTOP : MAX_PODS_TO_HYDRATE.MOBILE;
    const chunks = [];
    let currentChunk = [];
    let currentChunkPods = 0;

    visibleProductSets.forEach((set) => {
      // Start a new hydrator chunk when this set would exceed the pod limit
      if (currentChunkPods + set.productSetSize > maxPodsPerChunk && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentChunkPods = 0;
      }
      currentChunk.push(set);
      currentChunkPods += set.productSetSize;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  }, [visibleProductSets, isDesktop]);

  // SLOT RENDERING: Render promotional/content components at calculated positions
  const renderSlot = useCallback((slotIndex, endClampedOnly = false) => {
    const slotsAtPosition = componentsToRender?.[slotIndex];
    // Only the slots that opted in render at a position past the results.
    const slotComponents = endClampedOnly
      ? slotsAtPosition?.filter((slot) => slot?.clampToEnd)
      : slotsAtPosition;
    if (!slotComponents?.length) {
      return (
        <div className={isListLayout ? podListClass : 'sui-w-full'} data-testid={`slot-${slotIndex}`} />
      );
    }
    return slotComponents.map((slot, componentIndex) => (
      <div
        className={isListLayout ? podListClass : 'sui-w-full'}
        data-testid={`slot-${slotIndex}-${componentIndex}`}
        key={`slot-${slotIndex}-${componentIndex}`}
      >
        {slot?.component}
      </div>
    ));
  }, [componentsToRender, isListLayout, podListClass]);

  const renderPlaceholders = (podsCount = 8) => {
    const placeholderIds = Array.from({ length: podsCount }, (_unused, index) => `placeholder-${index + 1}`);
    return (
      <section
        className="sui-w-full sui-grid sui-grid-cols-12"
        data-component="ProductResultsPlaceholder"
        data-testid="product-pod-placeholder"
      >
        {placeholderIds.map((placeholderId) => (
          <Skeleton
            key={placeholderId}
            data-component="ProductPodPlaceholder"
            className="sm:sui-col-span-12 md:sui-col-span-4 lg:sui-col-span-3 sui-col-span-12"
          >
            <SkeletonBlock aspect="square" style={{ height: '250px' }}/>
            <SkeletonLine variant="multi" numberOfLines={7} />
          </Skeleton>
        ))}
      </section>
    );
  };

  // PRODUCT POD RENDERING: Render product pods with proper grid layout and error boundaries
  const renderPods = useCallback((startIndex, numberOfPods) => {
    let podsToRender = numberOfPods || 0;
    if (products && startIndex + numberOfPods > products.length) {
      podsToRender = products.length - startIndex;
    }
    if (!products || podsToRender <= 0) {
      return null;
    }

    return (
      <ProductPodGroup
        DetailsPod={CustomProductPodDetails}
        keyword={keyword}
      >
        {Array.from({ length: podsToRender }).map((_elem, idx) => {
          const itemIndex = startIndex + idx;
          const itemId = products[itemIndex].identifiers?.itemId;
          return (
            <ErrorBoundary
              id={`plp-product-pod__${itemIndex}`}
              index={itemIndex}
              key={`${itemId}-${itemIndex}`}
              name={`Product Pod: ${itemIndex}`}
            >
              {isListLayout ? (
                <div className={podListClass}>
                  {podFunction(products[itemIndex], itemIndex)}
                </div>
              ) : (
                <Col
                  className={isPep ? 'pep__pod !sui-pt-0' : 'browse-search__pod !sui-px-0 !sui-py-1 sm:!sui-p-1 empty:sui-py-0'}
                  fallback={isDesktop ? '6' : '12'}
                  lg="3"
                  md="3"
                  sm={isPep ? '3' : '4'}
                  xs={isPep ? '4' : '6'}
                >
                  {podFunction(products[itemIndex], itemIndex)}
                </Col>
              )}
            </ErrorBoundary>
          );
        })}
      </ProductPodGroup>
    );
  }, [products, keyword, isDesktop, podFunction, isListLayout]);

  return (
    <ResultsWrapped ref={ref}>
      <div
        id="plp-grid"
        className={isListLayout ? gridListClass : 'grid'}
        data-testid="plp-grid"
      >
        {/* PHASE 1: Initial placeholders (12 pods) shown during loading */}
        {shouldShowPlaceholders && renderPlaceholders(12)}

        {/* PHASE 2: Actual products with slot components when data loaded */}
        {!shouldShowPlaceholders && !!hydratorChunks?.length && (
          <QueryProvider
            cacheKey="search-product-pods"
            dataSource="searchNav"
            defaultVariables={enhancedDefaultVariables || defaultVariablesFallback}
            mounted={mountedFn}
            skip={skipFn}
          >
            <PodWrapper>
              {hydratorChunks.map((chunk, hydratorIndex) => {
                const isFirstChunk = hydratorIndex === 0;
                const isSecondChunk = hydratorIndex === 1;
                const chunkPodCount = chunk.reduce((sum, set) => sum + set.productSetSize, 0);
                return (
                  <Hydrator
                    key={`hydrator-${hydratorIndex}`}
                    id={`product-results-pods-${hydratorIndex + 1}`}
                    className="sui-w-full sui-flex sui-flex-wrap"
                    patch={isFirstChunk}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    immediateRender={isFirstChunk}
                    placeholder={isSecondChunk ? renderPlaceholders(chunkPodCount) : undefined}
                  >
                    {chunk.map((productSet) => (
                      <Fragment key={`productSet-${productSet.start}-${productSet.slotIndex}`}>
                        {renderPods(productSet.start, productSet.productSetSize)}
                        {renderSlot(productSet.slotIndex, productSet.endClamped)}
                      </Fragment>
                    ))}
                  </Hydrator>
                );
              })}
            </PodWrapper>
          </QueryProvider>
        )}
      </div>
    </ResultsWrapped>
  );
});

const PodWrapper = ({ children }) => {
  return children;
};

const createGridWithPod = (CustomPod) => {
  ProductPodGroup.dataModel = extend(ProductPodGroup.dataModel || {},
    CustomPod,
    CustomProductPodDetails);

  ProductResultsGrid.dataModel = extend({
    searchModel: params(
      {
        keyword: stringType(),
        navParam: stringType(),
        channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP'),
        storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),

      }
    ).shape({
      id: stringType(),
      products: params({ startIndex: numberType() }).arrayOf(CustomPod.dataModel.product),
    })
  },
  {
    getContent: params({ query: stringType() }).arrayOf(
      shapeType({
        title: stringType(),
        description: stringType(),
        imageUrl: stringType(),
        link: stringType(),
        type: stringType(),
        podTitle: stringType(),
        recordType: stringType()
      })
    )
  },
  useHybridData,
  ProductPodGroup,
  );

  PodWrapper.dataModel = extend(
    ProductResultsGrid
  );

  return ProductResultsGrid;
};

ProductResultsGrid.displayName = 'ProductResultsGrid';
ProductResultsGrid.propTypes = {
  defaultVariables: shape({}),
  podFunction: func.isRequired,
  pageType: string,
  keyword: string,
  storeId: string,
  itemId: string,
  productSSR: bool,
  searchSSR: bool,
  useTwoColumnPods: bool,
  slots: arrayOf(shape({
    position: shape({
      unprefixed: number, sm: number, md: number, lg: number, xl: number
    }),
    component: node,
    // Renders after the last product when the position is past the results.
    clampToEnd: bool
  })),
  experienceType: string
};

ProductResultsGrid.defaultProps = {
  pageType: null,
  keyword: null,
  defaultVariables: null,
  slots: null,
  storeId: null,
  itemId: null,
  productSSR: true,
  useTwoColumnPods: false,
  searchSSR: true,
  experienceType: null,
};

export { createGridWithPod };